import type { ReactNode } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  publishedTime?: string;
  modifiedTime?: string;
  structuredData?: Record<string, unknown>;
}

export function useDocumentMeta() {
  const setMeta = useCallback((props: SEOHeadProps) => {
    const {
      title,
      description,
      keywords,
      canonicalUrl,
      ogType = 'website',
      ogImage,
      ogImageWidth = 1200,
      ogImageHeight = 630,
      publishedTime,
      modifiedTime,
      structuredData,
    } = props;

    document.title = title;

    const setMetaTag = (name: string, content: string, attrName: string) => {
      let el: HTMLMetaElement | null = document.querySelector(
        attrName === 'property'
          ? 'meta[property="' + name + '"]'
          : 'meta[name="' + name + '"]'
      );
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrName, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const removeMetaTag = (name: string, attrName: string) => {
      const selector = attrName === 'property'
        ? 'meta[property="' + name + '"]'
        : 'meta[name="' + name + '"]';
      const el = document.querySelector(selector);
      if (el) el.remove();
    };

    setMetaTag('description', description, 'name');
    setMetaTag('keywords', keywords, 'name');

    setMetaTag('og:title', title, 'property');
    setMetaTag('og:description', description, 'property');
    setMetaTag('og:type', ogType, 'property');
    if (ogImage) {
      setMetaTag('og:image', ogImage, 'property');
      setMetaTag('og:image:width', String(ogImageWidth), 'property');
      setMetaTag('og:image:height', String(ogImageHeight), 'property');
    } else {
      removeMetaTag('og:image', 'property');
      removeMetaTag('og:image:width', 'property');
      removeMetaTag('og:image:height', 'property');
    }

    setMetaTag('twitter:card', 'summary_large_image', 'name');
    setMetaTag('twitter:title', title, 'name');
    setMetaTag('twitter:description', description, 'name');
    if (ogImage) {
      setMetaTag('twitter:image', ogImage, 'name');
    } else {
      removeMetaTag('twitter:image', 'name');
    }

    if (canonicalUrl) {
      let link: HTMLLinkElement | null = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonicalUrl;
    }

    if (publishedTime) {
      setMetaTag('article:published_time', publishedTime, 'property');
    } else {
      removeMetaTag('article:published_time', 'property');
    }

    if (modifiedTime) {
      setMetaTag('article:modified_time', modifiedTime, 'property');
    } else {
      removeMetaTag('article:modified_time', 'property');
    }

    const existingScript = document.getElementById('structured-data-json');
    if (existingScript) existingScript.remove();

    if (structuredData) {
      const script = document.createElement('script');
      script.id = 'structured-data-json';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData, null, 2);
      document.head.appendChild(script);
    }
  }, []);

  return { setMeta };
}